import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ProjectService, { Project, ProjectsResponse } from '../services/projectService';
import { useTenant } from './TenantContext';

interface ProjectContextType {
  currentProject: Project | null;
  availableProjects: Project[];
  isLoading: boolean;
  error: string | null;
  switchProject: (projectUuid: string | null) => void;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Storage key patterns: selected_project_${tenantId} and selected_project_name_${tenantId}
const getStorageKey = (tenantId: string) => `selected_project_${tenantId}`;
const getNameStorageKey = (tenantId: string) => `selected_project_name_${tenantId}`;

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const { currentTenant } = useTenant();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const projectService = ProjectService.getInstance();

  const loadProjects = async (tenantId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data: ProjectsResponse = await projectService.getProjects(tenantId);

      setAvailableProjects(data.projects);

      // Determine current project
      const storageKey = getStorageKey(tenantId);
      const nameStorageKey = getNameStorageKey(tenantId);
      const savedProjectUuid = localStorage.getItem(storageKey);
      let initialProject: Project | null = null;

      if (savedProjectUuid) {
        // Try to use saved project if still available
        initialProject = data.projects.find(p => p.uuid === savedProjectUuid) || null;
      }

      // If no saved project or not found, start with "All Projects" (null)
      setCurrentProject(initialProject);

      if (initialProject) {
        localStorage.setItem(storageKey, initialProject.uuid);
        localStorage.setItem(nameStorageKey, initialProject.alias);
      } else {
        // Clear the name if no project selected
        localStorage.removeItem(nameStorageKey);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
      // On error, set empty array and no current project
      setAvailableProjects([]);
      setCurrentProject(null);
    } finally {
      setIsLoading(false);
    }
  };

  const switchProject = (projectUuid: string | null) => {
    if (!currentTenant) return;

    const storageKey = getStorageKey(currentTenant.id);
    const nameStorageKey = getNameStorageKey(currentTenant.id);
    const previousProject = currentProject;

    if (projectUuid === null) {
      // "All Projects" mode
      if (currentProject !== null) {
        setCurrentProject(null);
        localStorage.removeItem(storageKey);
        localStorage.removeItem(nameStorageKey);

        // Emit custom event for context switch logging
        window.dispatchEvent(new CustomEvent('nomi:project-changed', {
          detail: {
            previous: previousProject?.alias || null,
            current: 'All Projects',
            projectUuid: null
          }
        }));
      }
    } else {
      const project = availableProjects.find(p => p.uuid === projectUuid);
      if (project && project.uuid !== currentProject?.uuid) {
        setCurrentProject(project);
        localStorage.setItem(storageKey, projectUuid);
        localStorage.setItem(nameStorageKey, project.alias);

        // Emit custom event for context switch logging
        window.dispatchEvent(new CustomEvent('nomi:project-changed', {
          detail: {
            previous: previousProject?.alias || 'All Projects',
            current: project.alias,
            projectUuid: project.uuid
          }
        }));
      }
    }
  };

  const refreshProjects = async () => {
    if (currentTenant) {
      await loadProjects(currentTenant.id);
    }
  };

  // Load projects when tenant changes
  useEffect(() => {
    if (currentTenant) {
      // CRITICAL: Clear current project IMMEDIATELY when tenant changes
      // This prevents sending stale project UUID from previous tenant during loading
      setCurrentProject(null);
      setAvailableProjects([]);
      loadProjects(currentTenant.id);
    } else {
      // No tenant selected - clear projects
      setAvailableProjects([]);
      setCurrentProject(null);
    }
  }, [currentTenant?.id]); // Re-fetch when tenant changes

  const value: ProjectContextType = {
    currentProject,
    availableProjects,
    isLoading,
    error,
    switchProject,
    refreshProjects,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
